import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  addRoom,
  BLACKOUT_LABELS,
  retireRoom,
  ROOM_LAYOUTS,
  type RoomDetail,
  type RoomLayout,
  roomsQuery,
  setRoomCapacities,
  OUTLET_KINDS,
  type OutletKind,
  setRoomOutlets,
  setRoomParts,
  updateRoomDetails,
  venueFilesQuery,
} from '../api/venues.ts';
import { installedGearQuery, type InstalledGear } from '../api/inventory.ts';
import { meQuery } from '../api/me.ts';
import { swmsQuery } from '../api/safety.ts';
import { Button } from '../components/button.tsx';
import { AddButton, EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { FactTile, Meter } from '../components/facts.tsx';
import { icons } from '../components/icons.ts';
import { Popover } from '../components/popover.tsx';
import { RoomPlans } from './venue-files.tsx';
import { Field } from '../components/field.tsx';
import { TextArea } from '../components/text-area.tsx';

// CB-REQ VEN-04 at v1.976 (venue slice 2A): what each room of a venue is like — its size, what it
// seats, how dark it gets, what is in the way — and how rooms combine: a whole room made of parts,
// one level deep. Recorded once here, read on every quote and job in the room.

/** Kilograms as a load: tonnes from 1,000 kg. */
const load = (kg: number) => (kg >= 1000 ? `${kg / 1000} t` : `${kg} kg`);

/** Millimetres as metres, e.g. 24000 → "24", 6500 → "6.5". */
const metres = (mm: number) => (mm / 1000).toString();

/** VEN-03 (venue slice 2B): "2 × three-phase 32 A (Stage left), 6 × single-phase 10 A". */
function powerLabel(r: RoomDetail): string | null {
  if (r.outlets.length === 0) return null;
  // "Three-phase 32 A" → "three-phase 32 A": only the first letter drops to lower case.
  const words = (k: OutletKind) => {
    const label = OUTLET_KINDS.find(([v]) => v === k)![1];
    return label[0]!.toLowerCase() + label.slice(1);
  };
  return r.outlets
    .map((o) => `${o.count} × ${words(o.kind)}${o.location ? ` (${o.location})` : ''}`)
    .join(', ');
}

/** "24 points at 9 m, 500 kg each; house riggers only". */
function riggingLabel(r: RoomDetail): string | null {
  const parts = riggingParts(r);
  return parts.length > 0 ? parts.join('; ') : null;
}

/** The rigging's facts one by one: "24 points at 9 m", "1 t each", "house riggers only". */
function riggingParts(r: RoomDetail): string[] {
  const g = r.rigging;
  const where = [
    g.points != null ? `${g.points} points` : null,
    g.heightMm != null ? `at ${metres(g.heightMm)} m` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return [
    where || null,
    g.swlKg != null ? `${load(g.swlKg)} each` : null,
    g.houseRigging ? 'house riggers only' : null,
    g.houseMotors ? 'house motors only' : null,
    g.notes ?? null,
  ].filter((p): p is string => !!p);
}

export type RoomsTool = 'add' | 'combine' | null;

/**
 * VEN-01 at v1.9104, the operator's Rooms board of 2026-10-26 (V-5): the rooms and combined rooms
 * as one table — size, best seats, power, rigging, VENUE EQUIP and files — beside the open room in
 * full. *+ Room* and *Combine…* sit on the tab bar; each opens its form in a popover, as Edit does.
 */
export function RoomsSection({
  venueId,
  mayManage,
  tool = null,
  onToolDone = () => {},
}: {
  venueId: string;
  mayManage: boolean;
  tool?: RoomsTool;
  onToolDone?: () => void;
}) {
  const rooms = useQuery(roomsQuery(venueId));
  // VEN-03 at v1.986: the venue's own gear in each room, from Inventory; absent without its view.
  const installed = useQuery(installedGearQuery(venueId));
  const files = useQuery(venueFilesQuery(venueId));
  const [openId, setOpenId] = useState<string | null>(null);
  const all = rooms.data ?? [];
  const open = all.find((r) => r.id === openId) ?? all[0];
  const filesIn = (roomId: string) =>
    (files.data ?? []).filter((f) => !f.supersededAt && f.room?.id === roomId).length;
  return (
    <section aria-labelledby="rooms" className="flex flex-col gap-3">
      <h2 id="rooms" className="sr-only">
        Rooms
      </h2>
      {tool === 'add' ? (
        <Popover title="New room" onClose={onToolDone}>
          <AddRoom venueId={venueId} onDone={onToolDone} />
        </Popover>
      ) : null}
      {tool === 'combine' ? (
        <Popover title="Combine rooms" onClose={onToolDone}>
          <CombineRooms venueId={venueId} rooms={all} onDone={onToolDone} />
        </Popover>
      ) : null}
      {rooms.isError ? (
        <p role="alert" className="alert">
          {rooms.error.message}
        </p>
      ) : null}
      {rooms.data?.length === 0 ? (
        <p className="text-sm text-ink-soft">
          No rooms yet. Add one here, or name one on a quote line.
        </p>
      ) : null}
      {open ? (
        <div className="table-and-panel">
          <div className="panel overflow-x-auto p-0">
            <table className="data-table min-w-[640px] text-sm">
              <caption className="sr-only">Rooms and combined rooms</caption>
              <thead>
                <tr>
                  <th scope="col" className="pl-3">
                    Room
                  </th>
                  <th scope="col">Size</th>
                  <th scope="col">Seats</th>
                  <th scope="col">Power</th>
                  <th scope="col">Rigging</th>
                  <th scope="col">VENUE EQUIP</th>
                  <th scope="col">Files</th>
                </tr>
              </thead>
              <tbody>
                {all.map((r) => {
                  const gear = installedIn(r, installed.data ?? []).gear;
                  return (
                    <tr key={r.id} aria-selected={r.id === open.id}>
                      <th scope="row" className="pl-3">
                        <button
                          type="button"
                          className="text-left font-semibold hover:underline"
                          aria-pressed={r.id === open.id}
                          onClick={() => setOpenId(r.id)}
                        >
                          {r.name}
                        </button>
                        {r.parts.length > 0 ? (
                          <span className="block text-xs font-normal text-muted">
                            Combined · {r.parts.map((p) => p.name).join(', ')}
                          </span>
                        ) : null}
                      </th>
                      <td>{planLabel(r) ?? '—'}</td>
                      <td>{bestSeats(r) ?? '—'}</td>
                      <td>{powerShort(r) ?? '—'}</td>
                      <td>{rigShort(r) ?? '—'}</td>
                      <td>{gear.length > 0 ? gear.reduce((n, g) => n + g.quantity, 0) : '—'}</td>
                      <td>{filesIn(r.id) || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <OpenRoom
            key={open.id}
            venueId={venueId}
            room={open}
            rooms={all}
            installed={installedIn(open, installed.data ?? [])}
            mayManage={mayManage}
            onOpen={setOpenId}
          />
        </div>
      ) : null}
    </section>
  );
}

/** "Theatre 480": the layout that seats the most. */
function bestSeats(r: RoomDetail): string | null {
  const best = [...r.capacities].sort((a, b) => b.seats - a.seats)[0];
  if (!best) return null;
  const layout = ROOM_LAYOUTS.find(([l]) => l === best.layout)![1].toLowerCase();
  return `${layout} ${best.seats.toLocaleString('en-AU')}`;
}

/** "3 × 32 A": the most of the largest outlet. */
function powerShort(r: RoomDetail): string | null {
  const top = OUTLET_KINDS.map(([k, label]) => ({
    label,
    count: r.outlets.filter((o) => o.kind === k).reduce((n, o) => n + o.count, 0),
  })).find((k) => k.count > 0);
  if (!top) return null;
  const amps = /\d+ A/.exec(top.label)?.[0] ?? top.label.toLowerCase();
  return `${top.count} × ${amps}`;
}

/** "24 · 1 t": points and the load on each. */
function rigShort(r: RoomDetail): string | null {
  const g = r.rigging;
  const parts = [
    g.points != null ? String(g.points) : null,
    g.swlKg != null ? load(g.swlKg) : null,
  ];
  return parts.some(Boolean) ? parts.filter(Boolean).join(' · ') : null;
}

function planLabel(r: RoomDetail): string | null {
  return r.lengthMm && r.widthMm ? `${metres(r.lengthMm)} × ${metres(r.widthMm)} m` : null;
}

/** The open room in full: its plans, size, seats, power, rigging, gear and what it combines with. */
function OpenRoom({
  venueId,
  room: r,
  rooms,
  installed,
  mayManage,
  onOpen,
}: {
  venueId: string;
  room: RoomDetail;
  rooms: readonly RoomDetail[];
  installed: { gear: readonly InstalledGear[]; fromParts: boolean };
  mayManage: boolean;
  onOpen: (roomId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireRoom(venueId, r.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey }),
  });
  const most = Math.max(0, ...r.capacities.map((c) => c.seats));
  // VEN-04 at v1.985: each layout stands alone. All from the parts says so once; a mix marks each
  // figure that is the parts added together.
  const mixed = r.capacities.some((c) => !c.fromParts);
  const shown = 3;
  return (
    <section className="panel flex flex-col gap-4 p-4" aria-labelledby="open-room">
      {editing ? (
        <Popover title={`Edit ${r.name}`} onClose={() => setEditing(false)}>
          <RoomForm venueId={venueId} room={r} rooms={rooms} onDone={() => setEditing(false)} />
        </Popover>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="open-room" className="flex-1 font-heading text-xl font-extrabold">
          {r.name}
        </h3>
        {mayManage ? (
          <>
            <EditButton what={r.name} onClick={() => setEditing(true)} />
            {retiring ? (
              <Button variant="quiet" disabled={retire.isPending} onClick={() => retire.mutate()}>
                Yes, retire {r.name}
              </Button>
            ) : (
              <Button variant="quiet" onClick={() => setRetiring(true)}>
                Retire<span className="sr-only"> {r.name}</span>
              </Button>
            )}
          </>
        ) : null}
      </div>
      {/* VEN-03 and VEN-04 (venue slice 2C): this room's live floor and rigging plans. */}
      <div className="rounded-sm bg-tile p-3 text-sm">
        <RoomPlans venueId={venueId} roomId={r.id} />
        <NoPlans venueId={venueId} roomId={r.id} />
      </div>
      <div className="fact-tiles fact-tiles-3">
        <FactTile value={planLabel(r)?.replace(' m', '') ?? '—'} label="Metres" />
        <FactTile value={r.heightMm ? `${metres(r.heightMm)} m` : '—'} label="Height" />
        <FactTile value={r.blackout ? BLACKOUT_SHORT[r.blackout] : '—'} label="Blackout" />
      </div>
      {r.obstructions ? <p className="text-sm">In the way: {r.obstructions}</p> : null}
      {r.capacities.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4 className="panel-label">
            Seats
            {r.capacities.every((c) => c.fromParts) ? (
              <span className="font-normal normal-case tracking-normal text-muted">
                {' '}
                (its parts added together)
              </span>
            ) : null}
          </h4>
          {r.capacities.map((c) => (
            <Meter
              key={c.layout}
              label={ROOM_LAYOUTS.find(([l]) => l === c.layout)![1]}
              fraction={most ? c.seats / most : 0}
              figure={
                <>
                  {c.seats.toLocaleString('en-AU')}
                  {mixed && c.fromParts ? (
                    <span className="text-muted"> (from its parts)</span>
                  ) : null}
                </>
              }
            />
          ))}
        </div>
      ) : null}
      {/* VEN-03 (venue slice 2B): power and rigging; a whole's from its parts, said so. */}
      {r.outlets.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4 className="panel-label">Power</h4>
          {r.outlets.map((o, i) => (
            <p key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <icons.power size={14} aria-hidden className="text-muted" />
              {o.count} × {OUTLET_KINDS.find(([k]) => k === o.kind)![1]}
              {o.location ? <span className="text-muted">{o.location}</span> : null}
            </p>
          ))}
          {r.outletsFromParts ? <p className="text-xs text-muted">From its parts</p> : null}
        </div>
      ) : null}
      {riggingLabel(r) ? (
        <div className="flex flex-col gap-1.5">
          <h4 className="panel-label">Rigging</h4>
          <p className="flex flex-wrap gap-x-3 text-sm">
            {riggingParts(r).map((part, i) => (
              <span key={i}>{i === 0 ? part[0]!.toUpperCase() + part.slice(1) : part}</span>
            ))}
            {r.rigging.fromParts ? (
              <span className="text-muted"> (from its parts, lowest height and load)</span>
            ) : null}
          </p>
          <VenueSwms venueId={venueId} />
        </div>
      ) : null}
      {installed.gear.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4 className="panel-label">
            Installed · VENUE EQUIP
            {installed.fromParts ? (
              <span className="font-normal normal-case tracking-normal text-muted">
                {' '}
                (from its parts)
              </span>
            ) : null}
          </h4>
          <p className="flex flex-wrap gap-1.5">
            {installed.gear.slice(0, shown).map((g) => (
              <span key={`${g.productId}-${g.tag ?? ''}`} className="tag">
                {g.tag ? `${g.productName} (${g.tag})` : `${g.quantity} × ${g.productName}`}
              </span>
            ))}
            {installed.gear.length > shown ? (
              <span
                className="tag"
                title={installed.gear
                  .slice(shown)
                  .map((g) => g.productName)
                  .join(', ')}
              >
                + {installed.gear.length - shown}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}
      {r.parts.length > 0 || r.partOf.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4 className="panel-label">{r.parts.length > 0 ? 'Made of' : 'Combines with'}</h4>
          {(r.parts.length > 0 ? r.parts : r.partOf).map((p) => {
            const whole = rooms.find((o) => o.id === p.id);
            const seats = whole ? bestSeats(whole) : null;
            return (
              <p key={p.id} className="text-sm">
                <button type="button" className="link" onClick={() => onOpen(p.id)}>
                  {p.name}
                </button>
                {seats ? <span className="text-muted"> · {seats}</span> : null}
              </p>
            );
          })}
        </div>
      ) : null}
      {r.generatorNotes || r.networkNotes ? (
        <dl className="dl text-sm">
          {r.generatorNotes ? (
            <>
              <dt>Generator</dt>
              <dd>{r.generatorNotes}</dd>
            </>
          ) : null}
          {r.networkNotes ? (
            <>
              <dt>Network</dt>
              <dd>{r.networkNotes}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {retire.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {retire.error.message}
        </p>
      ) : null}
    </section>
  );
}

const BLACKOUT_SHORT: Record<NonNullable<RoomDetail['blackout']>, string> = {
  full: 'Full',
  partial: 'Partial',
  none: 'None',
};

/** Said only when the room has no live plan, so the plan box is never empty. */
function NoPlans({ venueId, roomId }: { venueId: string; roomId: string }) {
  const files = useQuery(venueFilesQuery(venueId));
  const any = (files.data ?? []).some(
    (f) => f.kind !== 'photo' && !f.supersededAt && f.room?.id === roomId,
  );
  return any || !files.data ? null : <p className="text-muted">No floor plan</p>;
}

/**
 * VEN-04: a whole room made of parts, one level deep — a name and the rooms it combines, saved as a
 * new room and then its parts. A room that is already a whole cannot be a part.
 */
function CombineRooms({
  venueId,
  rooms,
  onDone,
}: {
  venueId: string;
  rooms: readonly RoomDetail[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [parts, setParts] = useState<string[]>([]);
  const offered = rooms.filter((r) => r.parts.length === 0);
  const save = useMutation({
    mutationFn: async () => {
      const whole = await addRoom(venueId, name.trim());
      await setRoomParts(venueId, whole.id, parts);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey }),
    onSuccess: onDone,
  });
  return (
    <form
      aria-label="Combine rooms"
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && parts.length >= 2) save.mutate();
      }}
    >
      <Field label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs text-ink-soft">Rooms it combines</legend>
        {offered.map((r) => (
          <label key={r.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={parts.includes(r.id)}
              onChange={(e) =>
                setParts(
                  e.currentTarget.checked ? [...parts, r.id] : parts.filter((id) => id !== r.id),
                )
              }
            />
            {r.name}
          </label>
        ))}
      </fieldset>
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !name.trim() || parts.length < 2}>
          Combine
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function AddRoom({ venueId, onDone }: { venueId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => addRoom(venueId, name.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
      onDone();
    },
  });
  return (
    <form
      aria-label="Add a room"
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) add.mutate();
      }}
    >
      <div className="min-w-56 flex-1">
        <Field label="Room’s name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      </div>
      <Button type="submit" disabled={add.isPending || !name.trim()}>
        Add
      </Button>
      <Button type="button" variant="quiet" onClick={onDone}>
        Cancel
      </Button>
      {add.error ? (
        <p role="alert" className="w-full text-sm font-semibold text-accent-2-700">
          {add.error.message}
        </p>
      ) : null}
    </form>
  );
}

/**
 * VEN-03 at v1.971: the venue's SWMS, listed beside the rigging (CMP-02, linked by venue). Only for
 * someone who may see shifts, who may read SWMS; for anyone else, nothing.
 */
function VenueSwms({ venueId }: { venueId: string }) {
  const me = useQuery(meQuery);
  const mayRead = me.data?.capabilities.includes('shifts.view') ?? false;
  const swms = useQuery({ ...swmsQuery, enabled: mayRead });
  const here = (swms.data ?? []).filter((d) => d.venueIds.includes(venueId));
  if (!mayRead || here.length === 0) return null;
  return (
    <p>
      SWMS for this venue:{' '}
      {here.map((d, i) => (
        <span key={d.id}>
          {i > 0 ? ', ' : ''}
          <Link to="/safety/$documentId" params={{ documentId: d.id }} className="link">
            {d.title}
          </Link>
        </span>
      ))}
    </p>
  );
}

/** Metres typed, millimetres sent; blank is not known. */
function toMm(text: string): number | null | 'bad' {
  if (!text.trim()) return null;
  const n = Number(text.trim().replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n <= 100 ? Math.round(n * 1000) : 'bad';
}

function RoomForm({
  venueId,
  room: r,
  rooms,
  onDone,
}: {
  venueId: string;
  room: RoomDetail;
  rooms: readonly RoomDetail[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(r.name);
  const [length, setLength] = useState(r.lengthMm ? metres(r.lengthMm) : '');
  const [width, setWidth] = useState(r.widthMm ? metres(r.widthMm) : '');
  const [height, setHeight] = useState(r.heightMm ? metres(r.heightMm) : '');
  const [blackout, setBlackout] = useState<NonNullable<RoomDetail['blackout']> | null>(
    r.blackout ?? null,
  );
  const [obstructions, setObstructions] = useState(r.obstructions ?? '');
  // A whole room's summed seats are not its own: the fields start empty for it.
  const [seats, setSeats] = useState<Record<RoomLayout, string>>(() => {
    const own = r.capacities.filter((c) => !c.fromParts);
    return Object.fromEntries(
      ROOM_LAYOUTS.map(([l]) => [l, own.find((c) => c.layout === l)?.seats.toString() ?? '']),
    ) as Record<RoomLayout, string>;
  });
  const [parts, setParts] = useState<string[]>(r.parts.map((p) => p.id));
  // VEN-03: a whole's figures from its parts are not its own, so the fields start empty for it.
  const ownRig = r.rigging.fromParts ? null : r.rigging;
  const [rigHeight, setRigHeight] = useState(ownRig?.heightMm ? metres(ownRig.heightMm) : '');
  const [rigPoints, setRigPoints] = useState(ownRig?.points?.toString() ?? '');
  const [rigSwl, setRigSwl] = useState(ownRig?.swlKg?.toString() ?? '');
  const [houseRigging, setHouseRigging] = useState(ownRig?.houseRigging ?? false);
  const [houseMotors, setHouseMotors] = useState(ownRig?.houseMotors ?? false);
  const [rigNotes, setRigNotes] = useState(ownRig?.notes ?? '');
  const [generatorNotes, setGeneratorNotes] = useState(r.generatorNotes ?? '');
  const [networkNotes, setNetworkNotes] = useState(r.networkNotes ?? '');
  const [outlets, setOutlets] = useState<
    Array<{ kind: OutletKind; count: string; location: string }>
  >(
    r.outletsFromParts
      ? []
      : r.outlets.map((o) => ({
          kind: o.kind,
          count: o.count.toString(),
          location: o.location ?? '',
        })),
  );
  const [problem, setProblem] = useState<string | undefined>();
  // One level deep: a part of another room is not combined, and a whole is not a part.
  const isPart = r.partOf.length > 0;
  const choosable = rooms.filter((o) => o.id !== r.id && o.parts.length === 0);

  const save = useMutation({
    mutationFn: async (input: {
      lengthMm: number | null;
      widthMm: number | null;
      heightMm: number | null;
      capacities: Array<{ layout: RoomLayout; seats: number }>;
      rigging: { heightMm: number | null; points: number | null; swlKg: number | null };
      outlets: Array<{ kind: OutletKind; count: number; location: string | null; notes: null }>;
    }) => {
      await updateRoomDetails(venueId, r.id, {
        name: name.trim(),
        lengthMm: input.lengthMm,
        widthMm: input.widthMm,
        heightMm: input.heightMm,
        blackout,
        obstructions: obstructions.trim() || null,
        riggingHeightMm: input.rigging.heightMm,
        riggingPoints: input.rigging.points,
        riggingPointSwlKg: input.rigging.swlKg,
        houseRiggingMandatory: houseRigging,
        houseMotorsMandatory: houseMotors,
        riggingNotes: rigNotes.trim() || null,
        generatorNotes: generatorNotes.trim() || null,
        networkNotes: networkNotes.trim() || null,
      });
      await setRoomCapacities(venueId, r.id, input.capacities);
      await setRoomOutlets(venueId, r.id, input.outlets);
      if (!isPart) await setRoomParts(venueId, r.id, parts);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
      onDone();
    },
    onError: async () => {
      // Some of it may have saved: show what the venue now holds.
      await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return setProblem('A room needs a name.');
    const [l, w, h] = [toMm(length), toMm(width), toMm(height)];
    if (l === 'bad' || w === 'bad' || h === 'bad') {
      return setProblem('Sizes are in metres, above nought, e.g. 24 or 6.5.');
    }
    const capacities: Array<{ layout: RoomLayout; seats: number }> = [];
    for (const [layout, label] of ROOM_LAYOUTS) {
      const text = seats[layout].trim();
      if (!text) continue;
      const n = Number(text);
      if (!Number.isInteger(n) || n <= 0) return setProblem(`${label}: a whole number of seats.`);
      capacities.push({ layout, seats: n });
    }
    // VEN-03: rigging figures are whole numbers above nought; blank is not known.
    const rh = toMm(rigHeight);
    if (rh === 'bad') return setProblem('Rigging height is in metres, above nought.');
    const whole = (text: string, label: string): number | null | 'bad' => {
      if (!text.trim()) return null;
      const n = Number(text.trim());
      if (Number.isInteger(n) && n > 0) return n;
      setProblem(`${label}: a whole number above nought.`);
      return 'bad';
    };
    const points = whole(rigPoints, 'Rigging points');
    if (points === 'bad') return;
    const swlKg = whole(rigSwl, 'Safe working load');
    if (swlKg === 'bad') return;
    const rows: Array<{ kind: OutletKind; count: number; location: string | null; notes: null }> =
      [];
    for (const o of outlets) {
      const count = whole(o.count, 'Each kind of outlet’s count');
      if (count === 'bad') return;
      if (count === null) continue;
      rows.push({ kind: o.kind, count, location: o.location.trim() || null, notes: null });
    }
    setProblem(undefined);
    save.mutate({
      lengthMm: l,
      widthMm: w,
      heightMm: h,
      capacities,
      rigging: { heightMm: rh, points, swlKg },
      outlets: rows,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label={`Room ${r.name}`}>
      <Field label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-1 text-xs text-ink-soft">Size, in metres</legend>
        <Field
          label="Length"
          inputMode="decimal"
          value={length}
          onChange={(e) => setLength(e.currentTarget.value)}
        />
        <Field
          label="Width"
          inputMode="decimal"
          value={width}
          onChange={(e) => setWidth(e.currentTarget.value)}
        />
        <Field
          label="Height"
          inputMode="decimal"
          value={height}
          onChange={(e) => setHeight(e.currentTarget.value)}
        />
      </fieldset>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`blackout-${r.id}`} className="text-xs text-ink-soft">
          Blackout
        </label>
        <select
          id={`blackout-${r.id}`}
          className="input"
          value={blackout ?? ''}
          onChange={(e) =>
            setBlackout(
              (e.currentTarget.value || null) as NonNullable<RoomDetail['blackout']> | null,
            )
          }
        >
          <option value="">Not known</option>
          {Object.entries(BLACKOUT_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <TextArea
        label="What is in the way (columns, low beams, chandeliers)"
        value={obstructions}
        onChange={(e) => setObstructions(e.currentTarget.value)}
      />
      <fieldset className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <legend className="mb-1 text-xs text-ink-soft">
          Seats, by layout{r.parts.length > 0 ? ' (blank uses its parts added together)' : ''}
        </legend>
        {ROOM_LAYOUTS.map(([layout, label]) => (
          <Field
            key={layout}
            label={label}
            inputMode="numeric"
            value={seats[layout]}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setSeats((s) => ({ ...s, [layout]: value }));
            }}
          />
        ))}
      </fieldset>
      {/* VEN-03 (venue slice 2B): power. No load is checked. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs text-ink-soft">
          Power outlets{r.outletsFromParts ? ' (none listed uses its parts added together)' : ''}
        </legend>
        {outlets.map((o, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_5rem] items-end gap-2 sm:grid-cols-[12rem_5rem_1fr_auto]"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`outlet-kind-${r.id}-${i}`} className="text-xs text-ink-soft">
                Kind
              </label>
              <select
                id={`outlet-kind-${r.id}-${i}`}
                className="input"
                value={o.kind}
                onChange={(e) => {
                  const kind = e.currentTarget.value as OutletKind;
                  setOutlets((list) => list.map((x, j) => (j === i ? { ...x, kind } : x)));
                }}
              >
                {OUTLET_KINDS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <Field
              label="How many"
              inputMode="numeric"
              value={o.count}
              onChange={(e) => {
                const count = e.currentTarget.value;
                setOutlets((list) => list.map((x, j) => (j === i ? { ...x, count } : x)));
              }}
            />
            <Field
              label="Where"
              value={o.location}
              onChange={(e) => {
                const location = e.currentTarget.value;
                setOutlets((list) => list.map((x, j) => (j === i ? { ...x, location } : x)));
              }}
            />
            <RemoveButton
              type="button"
              what={`outlet ${i + 1}`}
              onClick={() => setOutlets((list) => list.filter((_, j) => j !== i))}
            />
          </div>
        ))}
        <AddButton
          noun="Outlet"
          type="button"
          variant="quiet"
          className="self-start"
          onClick={() =>
            setOutlets((list) => [...list, { kind: '3ph_32a', count: '1', location: '' }])
          }
        />
      </fieldset>
      {/* VEN-03: rigging. */}
      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-1 text-xs text-ink-soft">
          Rigging
          {r.rigging.fromParts
            ? ' (blank uses its parts: lowest height and load, points added)'
            : ''}
        </legend>
        <Field
          label="Grid height (m)"
          inputMode="decimal"
          value={rigHeight}
          onChange={(e) => setRigHeight(e.currentTarget.value)}
        />
        <Field
          label="Points"
          inputMode="numeric"
          value={rigPoints}
          onChange={(e) => setRigPoints(e.currentTarget.value)}
        />
        <Field
          label="Safe working load per point (kg)"
          inputMode="numeric"
          value={rigSwl}
          onChange={(e) => setRigSwl(e.currentTarget.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={houseRigging}
            onChange={(e) => setHouseRigging(e.currentTarget.checked)}
          />
          House riggers only
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={houseMotors}
            onChange={(e) => setHouseMotors(e.currentTarget.checked)}
          />
          House motors only
        </label>
      </fieldset>
      <TextArea
        label="Rigging notes"
        value={rigNotes}
        onChange={(e) => setRigNotes(e.currentTarget.value)}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Generator"
          value={generatorNotes}
          onChange={(e) => setGeneratorNotes(e.currentTarget.value)}
        />
        <Field
          label="Network"
          value={networkNotes}
          onChange={(e) => setNetworkNotes(e.currentTarget.value)}
        />
      </div>
      {isPart ? (
        <p className="text-sm text-ink-soft">
          Part of {r.partOf.map((p) => p.name).join(' and ')}, so it does not combine others.
        </p>
      ) : choosable.length > 0 ? (
        <fieldset className="flex flex-col gap-1.5 text-sm">
          <legend className="mb-1 text-xs text-ink-soft">
            Made of (when this is a combined room)
          </legend>
          {choosable.map((o) => (
            <label key={o.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={parts.includes(o.id)}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setParts((p) => (on ? [...p, o.id] : p.filter((id) => id !== o.id)));
                }}
              />
              {o.name}
            </label>
          ))}
        </fieldset>
      ) : null}
      {problem || save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * VEN-03 and VEN-04 at v1.986: what is installed in a room; a combined room with none of its own
 * shows its parts', said so, as it does their power and rigging.
 */
function installedIn(
  room: RoomDetail,
  all: readonly InstalledGear[],
): { gear: InstalledGear[]; fromParts: boolean } {
  const own = all.filter((g) => g.roomId === room.id);
  if (own.length > 0 || room.parts.length === 0) return { gear: own, fromParts: false };
  const parts = new Set(room.parts.map((p) => p.id));
  const gear = all.filter((g) => parts.has(g.roomId));
  return { gear, fromParts: gear.length > 0 };
}
